import React, { useState, useRef } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Oval } from "react-loader-spinner";
import { TokenGuard } from "../App";
import { StandardURL } from '../services/constants';

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Bebas+Neue&family=DM+Sans:wght@300;400;500;600&display=swap');

  :root {
    --bg: #0a0a0a;
    --surface: #111111;
    --surface2: #1a1a1a;
    --border: #2a2a2a;
    --accent: #e8ff3c;
    --accent-dim: rgba(232,255,60,0.10);
    --text: #f0f0f0;
    --muted: #666666;
    --red: #ff4545;
  }

  * { box-sizing: border-box; margin: 0; padding: 0; }

  .auth-page {
    min-height: 100vh;
    background: var(--bg);
    display: flex;
    align-items: center;
    justify-content: center;
    font-family: 'DM Sans', sans-serif;
    padding: 2rem 1rem;
    position: relative;
    overflow: hidden;
  }
  .auth-page::before {
    content: '';
    position: absolute;
    inset: 0;
    background-image:
      linear-gradient(var(--border) 1px, transparent 1px),
      linear-gradient(90deg, var(--border) 1px, transparent 1px);
    background-size: 48px 48px;
    opacity: 0.35;
    pointer-events: none;
  }

  .auth-card {
    position: relative;
    z-index: 1;
    background: var(--surface);
    border: 1px solid var(--border);
    border-radius: 12px;
    padding: 2.8rem 2.5rem;
    width: 100%;
    max-width: 420px;
    box-shadow: 0 0 60px rgba(232,255,60,0.04);
  }

  .auth-logo {
    font-family: 'Bebas Neue', sans-serif;
    font-size: 2.2rem;
    letter-spacing: 0.1em;
    color: var(--accent);
    text-align: center;
    display: block;
    margin-bottom: 0.3rem;
  }
  .auth-title {
    font-family: 'Bebas Neue', sans-serif;
    font-size: 1.6rem;
    letter-spacing: 0.06em;
    color: var(--text);
    text-align: center;
    margin-bottom: 0.4rem;
  }
  .auth-subtitle {
    text-align: center;
    color: var(--muted);
    font-size: 0.82rem;
    margin-bottom: 2rem;
    line-height: 1.5;
  }
  .auth-divider {
    height: 1px;
    background: var(--border);
    margin: 1.6rem 0;
  }

  /* OTP boxes */
  .otp-row {
    display: flex;
    justify-content: center;
    gap: 0.75rem;
    margin-bottom: 2rem;
  }
  .otp-input {
    width: 52px;
    height: 58px;
    text-align: center;
    background: var(--surface2);
    border: 1px solid var(--border);
    border-radius: 8px;
    color: var(--text);
    font-family: 'Bebas Neue', sans-serif;
    font-size: 1.6rem;
    letter-spacing: 0.05em;
    outline: none;
    transition: border-color 0.2s, box-shadow 0.2s, background 0.2s;
    caret-color: var(--accent);
  }
  .otp-input:focus {
    border-color: var(--accent);
    box-shadow: 0 0 0 3px var(--accent-dim);
    background: #1e1e0e;
  }
  .otp-input:not(:placeholder-shown) {
    border-color: rgba(232,255,60,0.4);
  }

  .otp-actions {
    display: flex;
    gap: 0.75rem;
  }

  .auth-btn {
    flex: 1;
    background: var(--accent);
    color: #000;
    border: none;
    border-radius: 6px;
    padding: 0.78rem 1rem;
    font-family: 'Bebas Neue', sans-serif;
    font-size: 1rem;
    letter-spacing: 0.08em;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.4rem;
    transition: opacity 0.2s, transform 0.15s;
  }
  .auth-btn:hover:not(:disabled) { opacity: 0.88; transform: translateY(-1px); }
  .auth-btn:disabled { background: var(--surface2); color: var(--muted); cursor: not-allowed; }

  .auth-btn.secondary {
    background: var(--surface2);
    color: var(--text);
    border: 1px solid var(--border);
  }
  .auth-btn.secondary:hover:not(:disabled) {
    border-color: var(--text);
    background: var(--surface2);
  }

  .auth-footer-text {
    text-align: center;
    margin-top: 1.5rem;
    font-size: 0.8rem;
    color: var(--muted);
  }
  .auth-footer-text a {
    color: var(--accent);
    text-decoration: none;
    font-weight: 500;
  }
  .auth-footer-text a:hover { text-decoration: underline; }
`;

const OtpVerification = () => {
  const navigate = useNavigate();
  const [otpValues, setOtpValues] = useState(["", "", "", "", ""]);
  const [formData, setFormData] = useState({ code: "" });
  const refs = useRef([]);
  const [isLoading, setIsLoading] = useState(false);

  const handleChange = (index, value) => {
    const newOtpValues = [...otpValues];
    newOtpValues[index] = value;
    setOtpValues(newOtpValues);
    const otpCode = newOtpValues.join("");
    setFormData({ ...formData, code: otpCode });
    if (value && index < 4) {
      refs.current[index + 1].focus();
    }
  };

  const handleSubmit = async (e) => {
    setIsLoading(true);
    e.preventDefault();
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${StandardURL}/iMusic/auth/otp`,
        formData,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.response.token) {
        // Save to local storage
        localStorage.setItem("token", response.data.response.token);
        localStorage.setItem("user", JSON.stringify(response.data.response.user));
      }

      toast.success("OTP verification successful!");
      setIsLoading(false);

      // Check the role from the response and navigate accordingly
      const userRole = response.data.response.user?.role;

      if (userRole === "ADMIN") {
        navigate("../music/adminpage");
      } else {
        navigate("../music/landing");
      }

    } catch (error) {
      console.error(error);
      setIsLoading(false);
      toast.error("An error occurred, please try again later");
    }
  };

  const handleResendOtp = async () => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem("token");
      await axios.post(
        `${StandardURL}/iMusic/auth/resend-otp`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("OTP resent successfully!");
      setIsLoading(false);
    } catch (error) {
      console.error(error);
      setIsLoading(false);
      toast.error("An error occurred while resending OTP");
    }
  };

  return (
    <TokenGuard>
      <style>{styles}</style>
      <div className="auth-page">
        <div className="auth-card">
          <span className="auth-logo">MUSICA</span>
          <h2 className="auth-title">Verify Your Identity</h2>
          <p className="auth-subtitle">
            We sent a 5-digit code to your email.<br />Enter it below to continue.
          </p>
          <div className="auth-divider" />

          <form onSubmit={handleSubmit}>
            <div className="otp-row">
              {otpValues.map((value, index) => (
                <input
                  key={index}
                  type="text"
                  maxLength="1"
                  value={value}
                  onChange={(e) => handleChange(index, e.target.value)}
                  className="otp-input"
                  placeholder="·"
                  ref={(input) => (refs.current[index] = input)}
                />
              ))}
            </div>

            <div className="otp-actions">
              <button type="submit" className="auth-btn" disabled={isLoading}>
                {isLoading ? (
                  <Oval height={20} width={20} color="#000" visible={true} ariaLabel="loading-indicator" secondaryColor="#000" />
                ) : (
                  "Verify OTP"
                )}
              </button>
              <button
                type="button"
                className="auth-btn secondary"
                onClick={handleResendOtp}
                disabled={isLoading}
              >
                {isLoading ? (
                  <Oval height={20} width={20} color="white" visible={true} ariaLabel="loading-indicator" secondaryColor="white" />
                ) : (
                  "Resend"
                )}
              </button>
            </div>
          </form>

          <p className="auth-footer-text">
            Wrong account?{" "}
            <a href="/">Back to Login</a>
          </p>
        </div>
      </div>
    </TokenGuard>
  );
};

export default OtpVerification;